import React from "react";
import { FileAssignment, PolicyVendorContext } from "@/types";

// =============================================================================
// Types & Constants
// =============================================================================

/** Vendor assignment types supported by the dialog */
export type VendorAssignmentType = "tpaRx" | "carrier";

/** The client a file will bind to (#7551). `null` = none (chosen at review). */
export interface FileClientChoice {
  id: string;
  name: string;
}

/** Props for the prepare-upload panel and its Dialog wrapper. */
export interface VendorAssignmentDialogProps {
  open: boolean;
  files: FileAssignment[];
  targetSchema: string;
  onClose: () => void;
  onUpload: (files: FileAssignment[]) => Promise<void>;
  onRemoveFile: (fileId: string) => void;
  /** Type of vendor to assign - determines filtering and labels */
  vendorType?: VendorAssignmentType;
  /** Pre-select this vendor for all files when the dialog opens */
  defaultVendorId?: string;
  /**
   * Policy administering-vendor context (#5591). When supplied on a tpaRx selector,
   * the policy's TPA/Rx vendors render as radio choices with their role and rule
   * (#7552), the single primary TPA pre-fills, and "Different vendor…" reveals the
   * full search.
   */
  policyVendors?: PolicyVendorContext;
  /**
   * Names the source of the pre-fill (e.g. a policy id, or "the client's active
   * policies"). Kept for callers; no longer rendered since #7552 (the radio helper
   * text carries the fact).
   */
  defaultSourceLabel?: string;
  /**
   * The client the dialog chose for the batch (#7551). Each row starts on it and
   * can change its own via `onFileClientChange`. Undefined = no client picked;
   * rows then read "Chosen at review".
   */
  defaultClient?: FileClientChoice | null;
  /** Per-file client overrides keyed by file id (`null` = explicitly none). */
  fileClients?: Record<string, FileClientChoice | null>;
  onFileClientChange?: (fileId: string, client: FileClientChoice | null) => void;
  /** Inline mode (#7551): render Back at the footer's left. */
  onBack?: () => void;
  /** Inline mode: show the drop target again under the rows. */
  onAddMore?: () => void;
  /** True while the drop target is showing under the rows. */
  addingMore?: boolean;
  /** Inline mode: the drop target itself, rendered between the rows and the footer while adding more. */
  addMoreSlot?: React.ReactNode;
  /** Collapses the add-more drop target. */
  onDoneAdding?: () => void;
  /**
   * Inline mode: false hides the toolbar, rows and footer and shows only the
   * add-more slot, so the drop target has ONE tree position for the whole flow
   * (FilePond re-issues item ids on remount; !4974 thread 31126). Default true.
   */
  rowsVisible?: boolean;
  /** Fires when an upload starts / settles, so the parent dialog can refuse Escape meanwhile (#7551 AC11). */
  onUploadingChange?: (uploading: boolean) => void;
  /** False hides the per-row Change link (the client is fixed, e.g. launched from a Client page; #7551 AC3). Default true. */
  fileClientEditable?: boolean;
}

/** Configuration for each vendor type */
export const VENDOR_TYPE_CONFIG = {
  // TPA + RX union (AB#5312): claims uploads may be administered by a TPA, a PBM/RX
  // vendor, or a vendor that is both, so the dropdown must include all three.
  tpaRx: {
    label: "TPA / Rx Vendor",
    labelPlural: "TPA / Rx Vendors",
    resource: "vendors/tpa-rx",
  },
  carrier: {
    label: "Stop Loss Carrier",
    labelPlural: "Stop Loss Carriers",
    resource: "vendors/carriers",
  },
} as const;

/** Radio value for "Different vendor…" (#7552). */
export const OTHER_VENDOR = "__other__";

/** Map MIME type to a human-readable label */
export const getFileTypeLabel = (mimeType: string): string => {
  if (mimeType.includes("pdf")) return "PDF";
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "Excel";
  if (mimeType.includes("word")) return "Word";
  if (mimeType.includes("csv") || mimeType.includes("comma-separated")) return "CSV";
  return "Document";
};

/** True when the file is a multi-sheet workbook that requires a user pick. */
export const needsSheetSelection = (a: FileAssignment): boolean => (a.sheetNames?.length ?? 0) > 1;

/** True when the file already has at least one user-selected sheet. */
export const hasSheetSelection = (a: FileAssignment): boolean => (a.selectedSheets?.length ?? 0) > 0;
